import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { MultiSelect } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { RealmService } from '@/app/services/realm.service';
import { CompanyService } from '@/app/services/company.service';
import { CountryService } from '@/app/services/country.service';
import { AuthService } from '@/app/services/auth.service';
import { RealmSummary } from '@/lib-backend';
import { providerStatusView } from '@/app/utils/provider-status';

@Component({
    selector: 'app-website-list',
    standalone: true,
    imports: [
        DatePipe,
        Button,
        Dialog,
        IconField,
        InputIcon,
        InputText,
        MultiSelect,
        TableModule,
        Tag,
        Tooltip,
        FormsModule,
    ],
    templateUrl: './website-list.html',
    styleUrl: './website-list.scss',
})
export class WebsiteList implements OnInit {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly realmService = inject(RealmService);
    protected readonly companyService = inject(CompanyService);
    protected readonly countryService = inject(CountryService);
    private readonly authService = inject(AuthService);

    /**
     * Add Website + Company filter / multi-select require listing companies and
     * creating realms — both global-only on the backend. Realm-scoped admins see
     * a read-only list of their realms.
     */
    protected readonly canManage = this.authService.isGlobalAdmin;

    // ── Filters ─────────────────────────────────────────────────────────────
    protected readonly searchTerm = signal('');
    protected readonly selectedCompanyIds = signal<string[]>([]);

    private urlHydrated = false;

    protected readonly companyFilterOptions = computed(() =>
        this.companyService
            .companies()
            .filter((c) => c.DeletedAt === null)
            .map((c) => ({ label: c.Name, value: c.Id })),
    );

    protected readonly filteredRealms = computed(() => {
        const term = this.searchTerm().trim().toLowerCase();
        const companies = this.selectedCompanyIds();

        const matched = this.realmService.realms().filter((r) => {
            if (term) {
                const haystack = `${r.Domain} ${r.DisplayName}`.toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            if (companies.length > 0 && !companies.includes(r.CompanyId))
                return false;
            return true;
        });

        // Default sort: newest first. Pre-migration realms share a single
        // backfill timestamp (DateTimeOffset.UtcNow), so domain ascending is
        // the deterministic tiebreaker.
        return [...matched].sort((a, b) => {
            const created = b.CreatedAt.localeCompare(a.CreatedAt);
            return created !== 0 ? created : a.Domain.localeCompare(b.Domain);
        });
    });

    protected readonly hasActiveFilters = computed(
        () =>
            this.searchTerm().trim().length > 0 ||
            this.selectedCompanyIds().length > 0,
    );

    protected readonly providerStatusView = providerStatusView;

    // ── Sort (persisted across visits via localStorage) ─────────────────────
    private readonly initialSort = loadSavedSort();
    protected readonly sortField = signal<string>(this.initialSort.field);
    protected readonly sortOrder = signal<number>(this.initialSort.order);

    // ── Delete dialog state ─────────────────────────────────────────────────
    protected deleteDialogVisible = signal(false);
    protected deleteTarget = signal<RealmSummary | null>(null);
    protected deleteLoading = signal(false);
    protected deleteError = signal<string | null>(null);

    constructor() {
        // Sync filters → URL whenever they change (after initial hydration).
        effect(() => {
            const q = this.searchTerm().trim();
            const companies = this.selectedCompanyIds();
            if (!this.urlHydrated) return;

            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {
                    q: q || null,
                    company: companies.length > 0 ? companies.join(',') : null,
                },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        });
    }

    ngOnInit(): void {
        this.realmService.loadRealms();
        if (this.canManage()) {
            // listCompanies is global-only; skip for realm-scoped admins.
            this.companyService.loadCompanies();
        }

        this.route.queryParamMap
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((params) => {
                if (!this.urlHydrated) {
                    this.searchTerm.set(params.get('q') ?? '');
                    const company = params.get('company');
                    this.selectedCompanyIds.set(
                        company ? company.split(',').filter(Boolean) : [],
                    );
                    this.urlHydrated = true;
                }

                if (params.get('action') === 'add' && this.canManage()) {
                    this.openAdd();
                }
            });
    }

    /** Returns the company name + country for a realm, or null if the company is not loaded. */
    protected companyForRealm(
        r: RealmSummary,
    ): { name: string; country: string } | null {
        const c = this.companyService.byId().get(r.CompanyId);
        return c ? { name: c.Name, country: c.CountryCode } : null;
    }

    protected clearFilters(): void {
        this.searchTerm.set('');
        this.selectedCompanyIds.set([]);
    }

    /**
     * PrimeNG fires onSort on user clicks and on every value re-sort (e.g. when
     * filters change). Persist the field/order so the chosen sort survives
     * navigation and reloads.
     */
    protected onSortChange(event: {
        field?: string | string[] | null;
        order?: number;
    }): void {
        const field = Array.isArray(event.field) ? event.field[0] : event.field;
        if (!field || typeof event.order !== 'number') return;
        if (field === this.sortField() && event.order === this.sortOrder())
            return;
        this.sortField.set(field);
        this.sortOrder.set(event.order);
        persistSort(field, event.order);
    }

    protected exportCsv(): void {
        const rows = this.filteredRealms();
        const header = [
            'Created at',
            'Name',
            'Domain',
            'Category',
            'Site Template',
            'Version',
            'Company',
            'Country',
            'Realm ID',
        ];
        const escape = (v: string): string => {
            if (v == null) return '';
            const needsQuote = /[",\r\n]/.test(v);
            const safe = v.replace(/"/g, '""');
            return needsQuote ? `"${safe}"` : safe;
        };
        const lines = [
            header.map(escape).join(','),
            ...rows.map((r) => {
                const company = this.companyForRealm(r);
                return [
                    r.CreatedAt,
                    r.DisplayName,
                    r.Domain,
                    r.Category?.Name ?? '',
                    r.SiteTemplate?.Name ?? '',
                    r.SiteTemplate?.CurrentVersion ?? '',
                    company?.name ?? '',
                    company ? this.countryService.getName(company.country) : '',
                    r.RealmId,
                ]
                    .map(escape)
                    .join(',');
            }),
        ];
        const blob = new Blob([lines.join('\r\n')], {
            type: 'text/csv;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const ts = new Date().toISOString().slice(0, 10);
        const link = document.createElement('a');
        link.href = url;
        link.download = `websites-${ts}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    protected refresh(): void {
        this.realmService.loadRealms();
        this.companyService.loadCompanies();
    }

    // ── Add Website (full-page wizard) ──────────────────────────────────────

    protected openAdd(): void {
        this.router.navigate(['/website/new']);
    }

    // ── Row actions ─────────────────────────────────────────────────────────

    protected openEdit(realmId: string): void {
        this.router.navigate(['/website', realmId]);
    }

    protected openDeleteDialog(realm: RealmSummary): void {
        this.deleteTarget.set(realm);
        this.deleteError.set(null);
        this.deleteDialogVisible.set(true);
    }

    protected closeDeleteDialog(): void {
        this.deleteDialogVisible.set(false);
        this.deleteTarget.set(null);
    }

    protected async executeDelete(): Promise<void> {
        const realm = this.deleteTarget();
        if (!realm) return;
        this.deleteLoading.set(true);
        this.deleteError.set(null);
        try {
            const result = await this.realmService.deactivate(realm.RealmId);
            if (result === 'Ok') {
                this.closeDeleteDialog();
            } else {
                this.deleteError.set('Failed to delete website');
            }
        } catch {
            this.deleteError.set('Connection error. Please try again.');
        } finally {
            this.deleteLoading.set(false);
        }
    }

    protected trackByRealmId(_index: number, item: RealmSummary): string {
        return item.RealmId;
    }
}

const SORT_STORAGE_KEY = 'website-list-sort';

/** Restore the persisted table sort, defaulting to newest-first when absent/invalid. */
function loadSavedSort(): { field: string; order: number } {
    try {
        const raw = localStorage.getItem(SORT_STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as {
                field?: unknown;
                order?: unknown;
            };
            if (
                typeof parsed.field === 'string' &&
                (parsed.order === 1 || parsed.order === -1)
            ) {
                return { field: parsed.field, order: parsed.order };
            }
        }
    } catch {
        // ignore malformed JSON or unavailable storage
    }
    return { field: 'CreatedAt', order: -1 };
}

function persistSort(field: string, order: number): void {
    try {
        localStorage.setItem(
            SORT_STORAGE_KEY,
            JSON.stringify({ field, order }),
        );
    } catch {
        // ignore write failures (private mode / quota)
    }
}
